"use client";

import React, { useState, useEffect, useRef } from 'react';
import { Container } from '../container';
import CarCard from '../car-card';
import { testCarCardsData } from '@/shared/lib/car-cards-store';
import { useDragToScroll } from '@/shared/hooks';
import {Button} from "@/shared/components/ui/button";
import UniversalBookingModal from "@/shared/components/ui/universal-booking-modal";
import Image from 'next/image';

export const CarCardsSection: React.FC = () => {
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [isUniversalBookingOpen, setIsUniversalBookingOpen] = useState(false);
    const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastKeyRef = useRef<string | null>(null);
    const isFirstPressRef = useRef<boolean>(true);
    const {
        scrollRef,
        handleMouseDown,
        handleMouseUp,
        handleMouseMove,
        handleMouseLeave,
    } = useDragToScroll({
        scrollSpeed: 1.5,
        friction: 0.95,
        momentumThreshold: 0.5,
    });

    // Функция переключения карточек
    const switchCard = (direction: 'left' | 'right') => {
        setSelectedCardId((currentSelectedId) => {
            const currentIndex = currentSelectedId 
                ? testCarCardsData.findIndex(card => card.id === currentSelectedId)
                : -1;

            let newIndex: number;
            if (currentIndex === -1) {
                // Если ничего не выбрано, выбираем первую карточку
                newIndex = 0;
            } else if (direction === 'left') { 
                newIndex = currentIndex > 0 ? currentIndex - 1 : testCarCardsData.length - 1;
            } else {
                newIndex = currentIndex < testCarCardsData.length - 1 ? currentIndex + 1 : 0;
            }

            const newCardId = testCarCardsData[newIndex].id;
            
            // Прокрутка к выбранной карточке
            setTimeout(() => {
                const cardElement = cardRefs.current.get(newCardId);
                if (cardElement && scrollRef.current) {
                    const containerRect = scrollRef.current.getBoundingClientRect();
                    const cardRect = cardElement.getBoundingClientRect();
                    const scrollLeft = scrollRef.current.scrollLeft;
                    const cardLeft = cardRect.left - containerRect.left + scrollLeft;
                    const cardWidth = cardRect.width;
                    const containerWidth = containerRect.width;
                    const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2);

                    scrollRef.current.scrollTo({
                        left: scrollPosition,
                        behavior: 'smooth'
                    });
                }
            }, 0);

            return newCardId;
        });
    };

    // Проверка возможности прокрутки
    const checkScrollability = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    // Обработчик клика по стрелкам - использует ту же логику, что и клавиатура
    const handleArrowClick = (e: React.MouseEvent, direction: 'left' | 'right') => {
        e.preventDefault();
        e.stopPropagation();
        switchCard(direction);
        // Обновляем состояние кнопок после прокрутки
        setTimeout(() => {
            checkScrollability();
        }, 300);
    };

    // Обработка клавиш со стрелками с задержкой при удержании
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                
                const direction = e.key === 'ArrowLeft' ? 'left' : 'right';
                
                // Если это первое нажатие или другая клавиша
                if (isFirstPressRef.current || lastKeyRef.current !== e.key) {
                    isFirstPressRef.current = false;
                    lastKeyRef.current = e.key;
                    
                    // Мгновенное переключение при первом нажатии
                    switchCard(direction);
                    
                    // Запускаем интервал для повторяющихся нажатий
                    intervalRef.current = setInterval(() => {
                        switchCard(direction);
                    }, 300); // Задержка 300мс между переключениями
                }
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                // Останавливаем интервал при отпускании клавиши
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                isFirstPressRef.current = true;
                lastKeyRef.current = null;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    // Отслеживание возможности прокрутки
    useEffect(() => {
        // Небольшая задержка для корректного расчета размеров
        const timer = setTimeout(() => {
            checkScrollability();
        }, 100);
        
        const scrollElement = scrollRef.current;
        if (scrollElement) {
            scrollElement.addEventListener('scroll', checkScrollability);
            window.addEventListener('resize', checkScrollability);
        }
            
        return () => {
            clearTimeout(timer);
            if (scrollElement) {
                scrollElement.removeEventListener('scroll', checkScrollability);
                window.removeEventListener('resize', checkScrollability);
            }
        };
    }, []);

    // Обработка клика по экрану для снятия выделения
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Проверяем, что клик не по карточке или её содержимому
            const isClickOnCard = target.closest('.car-card-container');
            const isClickOnScroll = target.closest('.car-cards-scroll');
            const isClickOnArrow = target.closest('.navigation-arrow');
            
            // Игнорируем клики по стрелкам навигации
            if (isClickOnArrow) {
                return;
            }
            
            if (!isClickOnCard && !isClickOnScroll) {
                setSelectedCardId(null);
            } else if (isClickOnCard) {
                // Если клик по карточке, выбираем её
                const cardContainer = target.closest('.car-card-container');
                if (cardContainer) {
                    const cardId = cardContainer.getAttribute('data-card-id');
                    if (cardId) {
                        setSelectedCardId(cardId);
                    }
                }
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const setCardRef = (id: string, element: HTMLDivElement | null) => {
        if (element) {
            cardRefs.current.set(id, element);
        } else {
            cardRefs.current.delete(id);
        }
    };

    return (
        <>
        <section id="pricing" className="py-8 lg:py-16 bg-custom-black">
                <Container>
                    <div className="text-center mb-6 lg:mb-12">
                        <h2 className="text-white text-[24px] sm:text-[24px] md:text-[36px] lg:text-[45px] font-bold uppercase mb-4">
                            ТО МАСЛЯНЫЙ СЕРВИС
                        </h2>
                    </div>
                </Container>
                
                <div className="mx-auto w-full max-w-[1920px]">
                    <div className="relative px-[22px] md:px-[32px] lg:px-[42px] 2xl:px-[200px]">
                        <div 
                            ref={scrollRef}
                            className="flex items-stretch gap-6 overflow-x-auto car-cards-scroll cursor-grab select-none pb-6 scrollbar-hide pt-2"
                            onMouseDown={handleMouseDown}
                            onMouseUp={handleMouseUp}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                        >
                            {testCarCardsData.map((cardData) => (
                                <div 
                                    key={cardData.id} 
                                    ref={(el) => setCardRef(cardData.id, el)}
                                    className="flex-shrink-0 flex car-card-container"
                                    data-card-id={cardData.id}
                                >
                                    <CarCard 
                                        data={cardData}
                                        isSelected={selectedCardId === cardData.id}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Левая стрелка - вне padding, но внутри относительного контейнера */}
                        <button
                            onClick={(e) => handleArrowClick(e, 'left')}
                            className="navigation-arrow bg-custom-orange hover:bg-custom-light-orange transition-colors rounded-full w-12 h-12 lg:w-16 lg:h-16 shadow-lg flex items-center justify-center outline-none focus:outline-none focus-visible:outline-none absolute left-0 md:left-1 lg:left-2 top-1/2 -translate-y-1/2 z-10 hidden lg:flex"
                            aria-label="Предыдущая карточка"
                        >
                            <Image 
                                src="/images/arrow-white-right.svg" 
                                alt="left" 
                                width={18} 
                                height={18}
                                className="rotate-180 lg:w-8 lg:h-8 -translate-x-[2px] lg:-translate-x-[3px]"
                            />
                        </button>

                        {/* Правая стрелка - вне padding, но внутри относительного контейнера */}
                        <button
                            onClick={(e) => handleArrowClick(e, 'right')}
                            className="navigation-arrow bg-custom-orange hover:bg-custom-light-orange transition-colors rounded-full w-12 h-12 lg:w-16 lg:h-16 shadow-lg flex items-center justify-center outline-none focus:outline-none focus-visible:outline-none absolute right-0 md:right-1 lg:right-2 top-1/2 -translate-y-1/2 z-10 hidden lg:flex"
                            aria-label="Следующая карточка"
                        >
                            <Image 
                                src="/images/arrow-white-right.svg" 
                                alt="right" 
                                width={18} 
                                height={18}
                                className="lg:w-8 lg:h-8 translate-x-[2px] lg:translate-x-[3px]"
                            />
                        </button>
                    </div>
                </div>
                
                <Container>
                    <div className={'lg:text-center mt-14 lg:mt-30'}>
                        <h2 className={'text-custom-orange text-[30px] lg:text-[70px] font-bold leading-[1.2] uppercase'}>
                            все еще сомневаетесь?
                        </h2>
                        <p className={'text-white text-[24px] lg:text-[45px] leading-[1.2] uppercase mt-7'}>
                            Давайте рассчитаем стоимость вашей<br/> первой замены
                        </p>
                        <Button
                            variant="filledOrange"
                            className="mt-5 lg:mt-12 font-button py-2 lg:pt-5 lg:pb-5 px-12 lg:px-44 lg:mr-auto"
                            onClick={() => setIsUniversalBookingOpen(true)}
                        >
                            рассчитать стоимость
                        </Button>
                    </div>
                </Container>
        </section>
        <UniversalBookingModal isOpen={isUniversalBookingOpen} onClose={() => setIsUniversalBookingOpen(false)} blockName="Карточки автомобилей"/>
        </>
    );
};

export default CarCardsSection;